import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useEvidenceUpload, EvidenceItem } from '../../../hooks/useEvidenceUpload';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Checkbox } from '../../ui/checkbox';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../../lib/utils';
import DocumentIcon from './DocumentIcon';
import IconWrapper from '../../IconWrapper';
import { Timestamp } from '../../ui/timestamp';
import { FiUpload, FiTrash2, FiFile, FiInfo, FiCheck, FiRefreshCw } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { createSimulation } from '../../../utils/api';
import { useNavigate } from 'react-router-dom';

export interface EvidenceDrawerProps {
  signalId: string;
  orientation?: 'horizontal' | 'vertical';
  simulationId?: string;
  onEvidenceUploaded?: (evidenceId: string) => void;
  onEvidenceProcessing?: (evidenceId: string) => void;
  onEvidenceCompleted?: (evidenceId: string) => void;
  onEvidenceFailed?: (evidenceId: string, error: string) => void;
  onToggleEvidenceInReasoning?: (evidenceId: string, used: boolean) => void;
  onToggleUsedInReasoning?: (evidenceId: string, used: boolean) => void;
  onNewEvidenceDetected?: (hasNewEvidence: boolean) => void;
  className?: string;
}

interface EvidenceItemCardProps {
  item: EvidenceItem;
  getStatusBadge: (status: EvidenceItem['parsing_status'], error?: string | null) => React.ReactNode;
  formatFileSize: (bytes: number) => string;
  handleDelete: (evidenceId: string, filename: string) => void;
  isSelected?: boolean;
  onToggleSelect?: (evidenceId: string, selected: boolean) => void;
  showCheckbox?: boolean;
}

const EvidenceItemCard: React.FC<EvidenceItemCardProps> = ({
  item,
  getStatusBadge,
  formatFileSize,
  handleDelete,
  isSelected = false,
  onToggleSelect,
  showCheckbox = false
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  
  // Show summary if we have structured_text (markdown) or parsed_summary
  const hasSummary = !!(item.structured_text || item.parsed_summary?.summary);

  // Calculate popover position
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  
  useEffect(() => {
    if (showSummary && cardRef.current) {
      const updatePosition = () => {
        if (!cardRef.current) return;
        
        const rect = cardRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const popoverWidth = 320; // w-80 = 320px
        const spaceOnRight = viewportWidth - rect.right;
        const spaceOnLeft = rect.left;
        
        // Position to the right if there's space, otherwise to the left
        if (spaceOnRight >= popoverWidth + 16) {
          setPopoverStyle({
            left: `${rect.right + 8}px`,
            top: `${rect.top}px`,
            right: 'auto',
            transform: 'none',
          });
        } else if (spaceOnLeft >= popoverWidth + 16) {
          setPopoverStyle({
            right: `${viewportWidth - rect.left + 8}px`,
            top: `${rect.top}px`,
            left: 'auto',
            transform: 'none',
          });
        } else {
          // Center it if there's not enough space on either side
          setPopoverStyle({
            left: '50%',
            top: `${rect.bottom + 8}px`,
            right: 'auto',
            transform: 'translateX(-50%)',
          });
        }
      };
      
      updatePosition();
      
      // Update on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setPopoverStyle({});
    }
  }, [showSummary]);

  return (
    <div 
      ref={cardRef}
      className="group relative"
      onMouseEnter={() => hasSummary && setShowSummary(true)}
      onMouseLeave={() => setShowSummary(false)}
    >
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={cn(
          'flex items-start gap-3 p-3 rounded-lg border transition-colors',
          'hover:bg-muted/50',
          item.used_in_reasoning && 'border-primary/20 bg-accent'
        )}
      >
        {/* Checkbox for selection */}
        {showCheckbox && (
          <div className="flex-shrink-0 mt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onToggleSelect?.(item.id, checked === true)}
              aria-label={`Select ${item.filename}`}
            />
          </div>
        )}
        
        {/* Document Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <DocumentIcon
            type={item.document_type || item.content_type}
            size="md"
          />
        </div>

        {/* File Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground truncate" title={item.filename}>
              {item.filename}
            </p>
            {/* Visual indicator for used evidence - always visible */}
            {item.used_in_reasoning && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-primary">
                    <IconWrapper icon={FiCheck} className="w-2.5 h-2.5 text-white" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-xs">Used in simulation</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {getStatusBadge(item.parsing_status, item.parsing_error)}
          </div>

          {/* File size and upload time - hidden by default, shown on hover via info icon */}
          <div className="flex items-center gap-1 mt-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                  aria-label="File information"
                >
                  <IconWrapper icon={FiInfo} className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <div className="text-xs space-y-1">
                  <div>
                    <span className="font-medium">Size:</span> {formatFileSize(item.file_size)}
                  </div>
                  <div>
                    <span className="font-medium">Uploaded:</span>{' '}
                    <Timestamp value={item.created_at} format="relative" fallback="Unknown" hideTooltip />
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Delete Button */}
        <button
          onClick={() => handleDelete(item.id, item.filename)}
          className="flex-shrink-0 p-1 text-muted-foreground hover:text-destructive rounded transition-colors"
          aria-label="Delete evidence"
        >
          <IconWrapper icon={FiTrash2} className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Summary popover on hover - show structured_text (markdown) if available, otherwise summary */}
      {hasSummary && showSummary && (
        <div 
          ref={popoverRef}
          className="fixed z-50 max-h-96 w-80 overflow-y-auto rounded-md border border-[var(--go-warm-border)] bg-popover p-4 shadow-lg"
          style={popoverStyle}
          onMouseEnter={() => setShowSummary(true)}
          onMouseLeave={() => setShowSummary(false)}
          // Ensure popover is isolated from page content
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="text-sm">
            <div className="font-medium mb-2">Document Summary</div>
            {item.structured_text ? (
              <div className="text-xs text-muted-foreground prose prose-sm max-w-none prose-headings:text-sm prose-headings:font-semibold prose-p:text-xs prose-p:my-1 prose-ul:text-xs prose-ol:text-xs prose-li:text-xs prose-code:text-xs prose-pre:text-xs prose-strong:font-semibold">
                {/* Ensure we only render the exact structured_text string, not any DOM content */}
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  // Prevent ReactMarkdown from accessing DOM context
                  skipHtml={true}
                >
                  {String(item.structured_text || '')}
                </ReactMarkdown>
              </div>
            ) : item.parsed_summary?.summary ? (
              <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                {String(item.parsed_summary.summary || '')}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Summary not available
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EvidenceDrawer: React.FC<EvidenceDrawerProps> = ({
  signalId,
  orientation = 'horizontal',
  simulationId,
  onEvidenceUploaded,
  onEvidenceProcessing,
  onEvidenceCompleted,
  onEvidenceFailed,
  onToggleEvidenceInReasoning,
  onToggleUsedInReasoning,
  onNewEvidenceDetected,
  className
}) => {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<Set<string>>(new Set());
  const [isCreatingSimulation, setIsCreatingSimulation] = useState(false);
  
  const {
    evidence,
    loading,
    uploadProgress,
    uploadEvidence,
    deleteEvidence,
  } = useEvidenceUpload({
    signalId,
    simulationId,
    onEvidenceUploaded,
    onEvidenceProcessing,
    onEvidenceCompleted,
    onEvidenceFailed
  });

  // Track new evidence (has structured_text but not used_in_reasoning)
  const newEvidence = useMemo(() => {
    return evidence.filter(
      item => item.structured_text && 
      item.parsing_status === 'completed' && 
      !item.used_in_reasoning
    );
  }, [evidence]);

  // Track all completed evidence (both used and unused) - these are eligible for selection
  const allCompletedEvidence = useMemo(() => {
    return evidence.filter(
      item => item.structured_text && 
      item.parsing_status === 'completed'
    );
  }, [evidence]);

  // Auto-select ALL completed evidence by default when entering selection mode
  // This includes both previously used and new evidence
  // Track which evidence IDs we've seen to know when new evidence appears
  const seenEvidenceIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (newEvidence.length > 0 && simulationId && allCompletedEvidence.length > 0) {
      const currentEvidenceIds = new Set(allCompletedEvidence.map(e => e.id));
      const seen = seenEvidenceIds.current;
      
      // Check if there are any new evidence IDs we haven't seen before
      const newEvidenceIds = Array.from(currentEvidenceIds).filter(id => !seen.has(id));
      
      if (newEvidenceIds.length > 0) {
        // If nothing is selected, select all evidence (both used and new)
        // If something is already selected, add the new evidence to the selection
        setSelectedEvidenceIds(prev => {
          if (prev.size === 0) {
            // Nothing selected - select all evidence
            return new Set(currentEvidenceIds);
          } else {
            // Something already selected - add new evidence to selection
            const updated = new Set(prev);
            newEvidenceIds.forEach(id => updated.add(id));
            return updated;
          }
        });
        
        // Update seen evidence IDs
        currentEvidenceIds.forEach(id => seen.add(id));
      }
    }
  }, [allCompletedEvidence, newEvidence.length, simulationId]);

  // Notify parent when new evidence is detected
  useEffect(() => {
    onNewEvidenceDetected?.(newEvidence.length > 0);
  }, [newEvidence.length, onNewEvidenceDetected]);

  // Toggle evidence selection
  const handleToggleSelect = useCallback((evidenceId: string, selected: boolean) => {
    setSelectedEvidenceIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(evidenceId);
      } else {
        next.delete(evidenceId);
      }
      return next;
    });
  }, []);

  // Get selected evidence items (both used and unused)
  const selectedEvidence = useMemo(() => {
    if (selectedEvidenceIds.size === 0) return [];
    return Array.from(selectedEvidenceIds)
      .map(id => evidence.find(e => e.id === id))
      .filter((item): item is EvidenceItem => 
        !!item && 
        !!item.structured_text && 
        item.parsing_status === 'completed'
      );
  }, [selectedEvidenceIds, evidence]);

  const selectedCount = selectedEvidence.length;

  // Create new simulation with selected evidence
  const handleRunNewSimulation = useCallback(async () => {
    if (!signalId) return;
    
    // If nothing is selected, use all completed evidence (both used and unused)
    // If something is selected, use only the selected evidence
    const evidenceIdsToUse = selectedEvidenceIds.size > 0
      ? Array.from(selectedEvidenceIds)
      : allCompletedEvidence.map(e => e.id);
    
    if (evidenceIdsToUse.length === 0) {
      toast.error('No evidence available to include in the simulation');
      return;
    }

    setIsCreatingSimulation(true);
    try {
      // Create simulation with metadata indicating which evidence to use
      const simulation = await createSimulation(signalId, {
        evidence_ids: evidenceIdsToUse
      });
      
      // Validate response has id field
      if (!simulation || !simulation.id) {
        console.error('Invalid simulation response:', simulation);
        toast.error('Failed to create case: Invalid response from server');
        return;
      }

      toast.success('New case started');

      // Navigate to the new case
      navigate(`/v3/ombudsman/simulations/${simulation.id}`);
    } catch (error: any) {
      console.error('Failed to create case:', error);
      const errorMessage = error?.message || error?.error?.message || 'Unknown error';
      toast.error(`Failed to create case: ${errorMessage}`);
    } finally {
      setIsCreatingSimulation(false);
    }
  }, [signalId, selectedEvidenceIds, allCompletedEvidence, navigate]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    for (const file of files) {
      try {
        await uploadEvidence(file);
      } catch (error) {
        console.error('Failed to upload file:', error);
      }
    }
  }, [uploadEvidence]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      try {
        await uploadEvidence(file);
      } catch (error) {
        console.error('Failed to upload file:', error);
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadEvidence]);

  const handleDelete = useCallback(async (evidenceId: string, filename: string) => {
    if (!window.confirm(`Delete evidence "${filename}"?`)) return;
    
    try {
      await deleteEvidence(evidenceId);
    } catch (error) {
      console.error('Failed to delete evidence:', error);
    }
  }, [deleteEvidence]);

  const getStatusBadge = (status: EvidenceItem['parsing_status'], error?: string | null) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="text-xs">
            Pending
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="attention" className="text-xs">
            <div className="flex items-center gap-1">
              <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--go-bittersweet)]" />
              Processing...
            </div>
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="success" className="text-xs">
            Ready
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="text-xs">
            Failed {error ? `: ${error}` : ''}
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const containerClass = orientation === 'vertical'
    ? 'flex flex-col h-full'
    : 'flex flex-col w-full';

  const hasNewEvidence = newEvidence.length > 0;
  const showSelectionMode = !!(hasNewEvidence && simulationId);

  return (
    <div className={cn(containerClass, className)}>
      {/* Drag and Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50',
          orientation === 'vertical' ? 'mb-3 p-4' : 'mb-4'
        )}
      >
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,application/json,text/plain,text/csv"
          aria-label="Upload evidence files"
        />
        
        <div className="flex flex-col items-center justify-center text-center">
          <IconWrapper icon={FiUpload} className={cn('text-muted-foreground mb-2', orientation === 'vertical' ? 'h-6 w-6' : 'h-8 w-8')} />
          <p className={cn('font-medium text-foreground mb-1', orientation === 'vertical' ? 'text-[13px]' : 'text-sm')}>
            Drop files here or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-primary hover:underline"
            >
              browse
            </button>
          </p>
          <p className="text-[11px] text-muted-foreground">
            Policy documents, call logs, correspondence, screenshots, and complaint notes
          </p>
        </div>
      </div>

      {/* Upload Progress */}
      <AnimatePresence>
        {uploadProgress.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2 mb-4"
          >
            {uploadProgress.map((progress) => (
              <div
                key={progress.fileId}
                className="flex items-center gap-3 p-3 bg-muted rounded-lg"
              >
                <IconWrapper icon={FiFile} className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{progress.fileName}</p>
                  {progress.status === 'uploading' && (
                    <Progress value={progress.progress} className="mt-1 h-1" />
                  )}
                  {progress.status === 'processing' && (
                    <p className="text-xs text-muted-foreground mt-1">Processing...</p>
                  )}
                  {progress.status === 'failed' && (
                    <p className="text-xs text-destructive mt-1">{progress.error}</p>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Evidence List */}
      <div className={cn(
        'flex-1 overflow-y-auto',
        orientation === 'vertical' ? 'space-y-2' : 'space-y-2'
      )}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : evidence.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <IconWrapper icon={FiFile} className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">
              No evidence added yet
            </p>
          </div>
        ) : (
          <TooltipProvider>
            <AnimatePresence>
              {evidence.map((item) => (
                <EvidenceItemCard
                  key={item.id}
                  item={item}
                  getStatusBadge={getStatusBadge}
                  formatFileSize={formatFileSize}
                  handleDelete={handleDelete}
                  isSelected={selectedEvidenceIds.has(item.id)}
                  onToggleSelect={handleToggleSelect}
                  showCheckbox={showSelectionMode}
                />
              ))}
            </AnimatePresence>
          </TooltipProvider>
        )}
      </div>

      {/* Run New Simulation Button - Below evidence list */}
      {hasNewEvidence && simulationId && (
        <div className="mt-4">
          <Button
            onClick={handleRunNewSimulation}
            disabled={isCreatingSimulation || allCompletedEvidence.length === 0}
            size="sm"
            className="w-full disabled:cursor-not-allowed"
          >
            {isCreatingSimulation ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
                Creating...
              </>
            ) : (
              <>
                <IconWrapper icon={FiRefreshCw} className="w-3 h-3 mr-2" />
                {selectedCount > 0
                  ? `Get guidance (${selectedCount})`
                  : `Get guidance (${allCompletedEvidence.length})`}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EvidenceDrawer;
